import { useCallback, useEffect, useRef, useState } from "react";
import { useLatestRun } from "../api/useLatestRun";
import { resolveWidgetOrderWithBreaks } from "./dashboardOrder";

export interface DashboardWidget { key: string; visible: boolean }
export interface DashboardLayout<T extends DashboardWidget = DashboardWidget> {
  widgets: T[];
  widgetOrder: string[];
}
export interface DashboardLayoutAdapter<T extends DashboardWidget> {
  load: () => Promise<DashboardLayout<T>>;
  save: (layout: DashboardLayout<T>) => Promise<void>;
}

/** Shared layout lifecycle; products own only their catalogue and persistence adapter. */
export function useDashboardLayout<T extends DashboardWidget>(keys: readonly string[], adapter: DashboardLayoutAdapter<T>, enabled = true) {
  const [widgetStates, setWidgetStates] = useState<Partial<Record<string, T>>>({});
  const [widgetOrder, setWidgetOrder] = useState<string[]>([]);
  const [configMode, setConfigMode] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [saving, setSaving] = useState(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const snapshot = useRef<DashboardLayout<T> | null>(null);
  const editingRef = useRef(false);
  const savingRef = useRef(false);
  const startRun = useLatestRun();
  const apply = useCallback((layout: DashboardLayout<T>) => {
    setWidgetStates(Object.fromEntries(layout.widgets.filter(widget => keys.includes(widget.key)).map(widget => [widget.key, widget])));
    setWidgetOrder(resolveWidgetOrderWithBreaks(layout.widgetOrder, [...keys]));
    setReady(true);
  }, [keys]);
  const reload = useCallback(async () => {
    if (!enabled || editingRef.current) return;
    const isCurrent = startRun();
    try {
      const layout = await adapter.load();
      if (isCurrent() && !editingRef.current) { apply(layout); setError(null); }
    } catch (failure) { if (isCurrent()) setError(failure); }
    finally { if (isCurrent()) setLoading(false); }
  }, [adapter, apply, startRun, enabled]);
  useEffect(() => {
    // Initial synchronization with the remote preference store uses the same guarded loader.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void reload();
    return () => { startRun(); };
  }, [reload, startRun]);
  const layout = (): DashboardLayout<T> => ({ widgets: Object.values(widgetStates).filter((widget): widget is T => widget !== undefined), widgetOrder: resolveWidgetOrderWithBreaks(widgetOrder, [...keys]) });
  const begin = () => {
    if (!ready) return;
    snapshot.current = structuredClone(layout());
    editingRef.current = true;
    setConfigMode(true);
    setError(null);
  };
  const cancel = (): DashboardLayout<T> | null => {
    if (savingRef.current) return null;
    const previous = snapshot.current;
    if (previous) apply(previous);
    snapshot.current = null;
    editingRef.current = false;
    setConfigMode(false);
    setError(null);
    return previous;
  };
  const save = async (): Promise<boolean> => {
    if (savingRef.current || !ready || !editingRef.current) return false;
    savingRef.current = true;
    setSaving(true);
    try {
      await adapter.save(layout());
      apply(await adapter.load());
      snapshot.current = null;
      editingRef.current = false;
      setConfigMode(false);
      setError(null);
      return true;
    } catch (failure) { setError(failure); return false; }
    finally { savingRef.current = false; setSaving(false); }
  };
  const isWidgetVisible = (key: string) => widgetStates[key]?.visible !== false;
  const setWidgetVisible = (key: string, visible: boolean) => setWidgetStates(previous => {
    const widget = previous[key];
    return widget ? { ...previous, [key]: { ...widget, visible } } : previous;
  });
  return { widgetStates, setWidgetStates, widgetOrder, setWidgetOrder, configMode, loading, saving, ready, error, begin, cancel, save, reload, isWidgetVisible, setWidgetVisible };
}
